// JSON-LD generators for community posts.
// Strategy chosen 2026-05-08:
//   - review / free → DiscussionForumPosting (Google 2024 official, UGC fits)
//   - qna           → QAPage  (Question + first comment as suggestedAnswer)
//   - notice        → Article (operator-published, not UGC)

const SITE_URL = 'https://koa-asia.com';
const SITE_NAME = 'KOA DETAILING';

function postUrl(postId) {
  return `${SITE_URL}/community/post/${encodeURIComponent(postId)}`;
}

function authorBlock(post) {
  return { '@type': 'Person', name: post.nickname || '익명' };
}

function discussionForumPosting(post) {
  return {
    '@context': 'https://schema.org',
    '@type': 'DiscussionForumPosting',
    headline: post.title,
    text: post.body,
    url: postUrl(post.postId),
    datePublished: post.createdAt,
    dateModified: post.updatedAt || post.createdAt,
    author: authorBlock(post),
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/CommentAction',
      userInteractionCount: post.commentCount || 0,
    },
    image: Array.isArray(post.imageUrls) && post.imageUrls.length > 0 ? post.imageUrls : undefined,
  };
}

// QAPage with Question + (optional) suggestedAnswer pulled from first comment.
// suggestedAnswers wiring requires comments — caller can pass them in `comments`
// array for richer markup. With no comments we still emit a valid Question.
function qaPage(post, comments) {
  const suggested = (comments || []).slice(0, 3).map(c => ({
    '@type': 'Answer',
    text: c.body,
    dateCreated: c.createdAt,
    author: { '@type': 'Person', name: c.nickname || '익명' },
    upvoteCount: 0,
    url: postUrl(post.postId),
  }));
  return {
    '@context': 'https://schema.org',
    '@type': 'QAPage',
    mainEntity: {
      '@type': 'Question',
      name: post.title,
      text: post.body,
      dateCreated: post.createdAt,
      author: authorBlock(post),
      answerCount: post.commentCount || 0,
      upvoteCount: 0,
      ...(suggested.length > 0 ? { suggestedAnswer: suggested } : {}),
    },
  };
}

function articleJsonLd(post) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    articleBody: post.body,
    url: postUrl(post.postId),
    datePublished: post.createdAt,
    dateModified: post.updatedAt || post.createdAt,
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    image: Array.isArray(post.imageUrls) && post.imageUrls.length > 0 ? post.imageUrls : undefined,
  };
}

export function buildPostJsonLd(post, comments) {
  if (!post) return null;
  switch (post.category) {
    case 'qna':    return qaPage(post, comments);
    case 'notice': return articleJsonLd(post);
    case 'review':
    case 'free':
    default:       return discussionForumPosting(post);
  }
}

export function buildBreadcrumb(post) {
  if (!post) return null;
  const categoryName = {
    review: '후기', qna: 'Q&A', free: '자유', notice: '공지',
  }[post.category] || post.category;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '홈', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '커뮤니티', item: `${SITE_URL}/community` },
      { '@type': 'ListItem', position: 3, name: categoryName, item: `${SITE_URL}/community/${post.category}` },
      { '@type': 'ListItem', position: 4, name: post.title, item: postUrl(post.postId) },
    ],
  };
}
